// Skelett statt Spinner (ST-UX-001, Slice 9; NFR-UX-004, WCAG 2.2.2).
//
// Das Skelett hat die Hoehe dessen, was kommt; so springt beim Ankommen des
// Inhalts nichts. Fluent `Skeleton` ist ein `progressbar` mit `aria-busy`, damit
// wartet `layoutRuhig` weiter richtig auf das Ladeende.
//
// Der Ladesatz steht als optisch verborgener TEXT im Skelett und benennt das
// progressbar per aria-labelledby. Als reines aria-label waere ein Warten auf
// `getByText(/wird geladen/)` sofort erfuellt, weil kein Textknoten existiert.

import { useId, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { Skeleton, SkeletonItem, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

export interface SkelettZeile {
  /** Hoehe in px bei breiter Ansicht. */
  readonly hoehe: number;
  /** Hoehe in px bei schmaler Ansicht (unter 40 rem); sonst wie `hoehe`. */
  readonly schmal?: number;
  /**
   * Statt eines Balkens zwei Kartenumrisse nebeneinander (schmale Datenkarte,
   * breiter Verlauf), schmal untereinander -- jeder mit `hoehe`.
   */
  readonly karten?: true;
  /** Statt eines Balkens zwei Reiter ueber einer Linie, wie die Registerleiste (Ueberblick, Verlauf). */
  readonly register?: true;
}

// Die Registerleiste (40 px, als Reiter) und darunter die zwei Karten des Ueberblicks
// als Umrisse (L13-08) statt Vollbalken; die Kopfkarte darueber ist
// `KopfSkelett` (screens/kontakt/kopf-skelett.tsx) und folgt ihrem Vorbild selbst.
export const SKELETT_KONTAKT: readonly SkelettZeile[] = [{ hoehe: 40, register: true }, { hoehe: 240, karten: true }];
export const SKELETT_VERLAUF: readonly SkelettZeile[] = [
  { hoehe: 124, schmal: 180 },
  { hoehe: 124, schmal: 180 },
  { hoehe: 124, schmal: 180 },
];
export const SKELETT_ADRESSEN: readonly SkelettZeile[] = [{ hoehe: 156 }];
export const SKELETT_WIEDERVORLAGEN: readonly SkelettZeile[] = [
  { hoehe: 68, schmal: 88 },
  { hoehe: 68, schmal: 88 },
  { hoehe: 68, schmal: 88 },
  { hoehe: 68, schmal: 88 },
];
// Sammlungen (ST-SEA-002 Plan F3, Schnitt 3a): je Zeile Link und Captionzeile;
// die Ansicht dazu ihr Kopf aus Name und Zahl.
export const SKELETT_SAMMLUNGEN: readonly SkelettZeile[] = [{ hoehe: 60 }, { hoehe: 60 }, { hoehe: 60 }];
export const SKELETT_SAMMLUNG: readonly SkelettZeile[] = [{ hoehe: 72 }, { hoehe: 60 }, { hoehe: 60 }, { hoehe: 60 }];

const useStyles = makeStyles({
  skelett: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    // Ohne Bewegung, wenn die Nutzerin sie abgewaehlt hat (WCAG 2.3.3).
    '@media (prefers-reduced-motion: reduce)': {
      '& *': { animationName: 'none' },
      '& *::before': { animationName: 'none' },
      '& *::after': { animationName: 'none' },
    },
  },
  zeile: {
    height: 'var(--skelett-hoehe)',
  },
  // Dieselbe Regel wie der Ueberblick (screens/kontakt/stile.ts, kontaktpanel):
  // gemessen an der Breite des Inhalts, nicht des Fensters -- neben der Navigation
  // ist er schmaler als das Fenster, und sonst wechselten die Karten beim Ankommen
  // die Anordnung.
  kartenRahmen: { containerType: 'inline-size', containerName: 'skelettpanel' },
  karten: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '1rem',
    '@container skelettpanel (min-width: 44rem)': {
      gridTemplateColumns: 'minmax(17.5rem, 23.75rem) minmax(26rem, 1fr)',
    },
  },
  umriss: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    padding: '1rem',
    boxSizing: 'border-box',
    height: 'var(--skelett-hoehe)',
    // Flaeche, Linie und Schatten wie die Karten, die kommen (screen-styles card).
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
  },
  // Die Reiter sitzen unten auf der Linie der Registerleiste; schmal laufen sie nicht ueber.
  register: {
    display: 'flex',
    alignItems: 'center',
    gap: '1.5rem',
    height: 'var(--skelett-hoehe)',
    overflow: 'hidden',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Etwa so breit wie die Beschriftungen der zwei Register, damit beim Ankommen nichts springt.
  reiter: { width: '5.5rem', height: '0.75rem', flexShrink: 0 },
  linie: { height: '0.75rem' },
  kurz: { width: '60%' },
  // Die uebrigen Skelette wechseln bei 40 rem des Fensters.
  schmal40: { '@media (max-width: 40rem)': { height: 'var(--skelett-hoehe-schmal)' } },
  verborgen: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
});

/** Der Ladesatz fuer die Statusregion: hoerbar, ohne Einfluss auf das Layout. */
export function Ladeansage({ satz }: { readonly satz: string }): ReactElement {
  const own = useStyles();
  return <span className={own.verborgen}>{satz}</span>;
}

export function Skelett({
  ladesatz,
  zeilen,
  kinder,
}: {
  readonly ladesatz: string;
  readonly zeilen: readonly SkelettZeile[];
  /** Vor den Zeilen, im selben `Skeleton`: es bleibt bei einem progressbar mit einem Ladesatz. */
  readonly kinder?: ReactNode;
}): ReactElement {
  const own = useStyles();
  const satzId = useId();
  return (
    <Skeleton className={own.skelett} aria-labelledby={satzId}>
      <span id={satzId} className={own.verborgen}>
        {ladesatz}
      </span>
      {kinder}
      {zeilen.map((zeile, nummer) =>
        zeile.register === true ? (
          <div key={nummer} className={own.register} style={{ '--skelett-hoehe': `${zeile.hoehe}px` } as CSSProperties} data-skelett="register">
            {[0, 1].map((reiter) => (
              <SkeletonItem key={reiter} shape="rectangle" className={own.reiter} />
            ))}
          </div>
        ) : zeile.karten === true ? (
          <div key={nummer} className={own.kartenRahmen}>
            <div className={own.karten} style={{ '--skelett-hoehe': `${zeile.hoehe}px` } as CSSProperties}>
              {[0, 1].map((karte) => (
                <div key={karte} className={own.umriss} data-skelett="umriss">
                  <SkeletonItem shape="rectangle" className={mergeClasses(own.linie, own.kurz)} />
                  <SkeletonItem shape="rectangle" className={own.linie} />
                  <SkeletonItem shape="rectangle" className={own.linie} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <SkeletonItem
            key={nummer}
            shape="rectangle"
            className={mergeClasses(own.zeile, own.schmal40)}
            style={{ '--skelett-hoehe': `${zeile.hoehe}px`, '--skelett-hoehe-schmal': `${zeile.schmal ?? zeile.hoehe}px` } as CSSProperties}
          />
        ),
      )}
    </Skeleton>
  );
}
